import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash';
import classNames from 'classnames';
import Helmet from 'react-helmet';
import isInStock from '../utils/isInStock';
import getChildSku from '../utils/getChildSku';

import Banners from './partials/Banners';
import Categories from './partials/Categories';
import Disclaimer from './partials/Disclaimer';

import BopBasicInfo from '../containers/BopBasicInfo';
import SimilarProducts from './partials/SimilarProducts';
import AdvertisedProducts from '[theme-lib]/apps/products/components/partials/AdvertisedProducts';
import BopGallery from './gallery/BopGallery';
import RecipesList from './partials/RecipesList';
import Reviews from '../containers/Reviews';
import Title from '../containers/Title';
import YouMightLike from './partials/YouMightLike';

import BopPageComponent from './BopPageComponent';
import FeedbackForm from './FeedbackForm';

import { BetaFeatures as FoodProfileBetaFeatures } from 'apps/foodProfile/consts';
import PageViewType from 'apps/analytics/components/PageViewType';
import pageTypes from 'apps/analytics/utils/pageTypes';
import ProductLabel from 'apps/products/models/ProductLabel';
import EnforceLocation from 'apps/meta/containers/EnforceLocation';
import { getEnforcedProductDetailsLink } from '../links';
import NotFound from 'widgets/NotFound/NotFound';
import Breadcrumbs from 'widgets/Breadcrumbs/Breadcrumbs';

import { DynamicYieldPageContext, PageContextType } from 'apps/dynamicYield';

import { isBreadcrumbsWithNewUI } from 'lib/features';

import ProductInfo from './partials/ProductInfo';
import { getCatalogueLink } from 'apps/catalogue/links';
import { isAlcohol } from 'apps/products/utils/alcohol';
import { gtm } from 'apps/gtm/index';
import PageViewEvent from 'apps/analytics/containers/PageViewEvent';

import { config } from 'api';

export default class Bop extends Component {
  static propTypes = {
    analyticsContextData: PropTypes.object,
    params: PropTypes.shape({
      sku: PropTypes.string.isRequired,
    }).isRequired,
    isFlashSale: PropTypes.bool,
    isLoggedIn: PropTypes.bool.isRequired,
    product: PropTypes.object,
    productDetails: PropTypes.shape({
      backOfPack: PropTypes.shape({
        offersDetails: PropTypes.object,
        images: PropTypes.array,
        videos: PropTypes.array,
      }),
      similarProducts: PropTypes.object,
      recommendations: PropTypes.object,
      recipes: PropTypes.array,
      criteoSponsoredProducts: PropTypes.shape({
        fopCollection: PropTypes.object,
        pageBeacon: PropTypes.string,
      }),
    }),
    deliverySlot: PropTypes.object,
    turnOffEnforceLocation: PropTypes.bool,
    router: PropTypes.object.isRequired,
    productBanners: PropTypes.object,
    betaFeatures: PropTypes.array.isRequired,
    feedbackCorrectionTypes: PropTypes.arrayOf(PropTypes.string),
    postFeedback: PropTypes.func,
    productsBySku: PropTypes.object,
    location: PropTypes.object.isRequired,
    pageMetaData: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    disclaimerRegularMessage: PropTypes.string,
  };

  componentDidMount() {
    const {
      product,
      productDetails,
    } = this.props;
    if (product) {
      const {
        brand,
        name,
        mainCategory,
        price,
        sku,
        offer = {},
        labels = [],
        packInfo = {},
      } = product;
      const productRating = get(product, 'reviewStats.averageRate', 0);
      const productDescription = get(productDetails, 'backOfPack.bopFields.description', [])
        .map(item => item.content)
        .join(' ');
      const productNutrition = get(productDetails, 'backOfPack.bopFields.nutrition', [])
        .map(item => item.content)
        .join(' ');
      const productIngredients = get(productDetails, 'backOfPack.bopFields.ingredients', [])
        .map(item => item.content)
        .join(' ');
      const productUsage = get(productDetails, 'backOfPack.bopFields.storageAndUsage', [])
        .map(item => item.content)
        .join(' ');
      const dietaryFlags = [
        ...(packInfo.vegetarian ? ['Vegetarian'] : []),
        ...(packInfo.organic ? ['Organic'] : []),
      ].join(', ');

      gtm.trigger('gtm:singleProductImpression', {
        id: sku,
        name: name.replace(/\s/g, '-'),
        brand: get(brand, 'name', ''),
        price: price.current,
        category: mainCategory,
        dimension60: productRating > 0 ? productRating.toFixed(1) : undefined, // product rating
        promotionNames: offer.text,
        dimension62: get(productDetails, 'backOfPack.images.length'), // product image count
        dimension63: get(productDetails, 'backOfPack.videos.length'), // product video count
        dimension64: productDescription.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product desctiption
        dimension65: productNutrition.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product nutritional information
        dimension66: dietaryFlags || undefined, // product dietary flags
        dimension67: productIngredients.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product ingridients
        dimension68: productUsage.replace(/<\/?[^>]+(>|$)/g, '') || undefined, // product usage
        dimension69: price.previous, // product full price
        dimension70: get(product, 'coupon.id'), // product coupon
        canAddToBasket: !labels.includes(ProductLabel.OOS),
        isFavorite: labels.includes(ProductLabel.FAVOURITE),
        isFeatured: undefined,
        isOnPromotion: labels.includes(ProductLabel.ON_OFFER),
        promotionIds: offer.id,
        list: 'BOP',
      });
    }
  }

  hasDerivatives = () => {
    const { productDetails: { backOfPack } = {} } = this.props;
    return !!(backOfPack && backOfPack.derivatives);
  };

  shouldUpdateUrl = () => {
    const { productDetails, turnOffEnforceLocation } = this.props;
    const { backOfPack = {} } = this.props.productDetails;
    return !turnOffEnforceLocation && !(backOfPack.derivatives && getChildSku(productDetails));
  };

  renderBreadcrumbs = () => {
    const { product, betaFeatures } = this.props;
    const { name } = product;
    const tags = get(this.props, 'productDetails.backOfPack.tags', null);
    const showBreadcrumbsConfig = get(config, 'bop.showBreadcrumbs', true);
    const mainCategoryTags = tags && tags.categories &&
      (tags.categories.find(tag => tag.primary) || tags.categories[0]);

    /*
      Breadcrumbs on BOP should be hidden only for Ocado customers which new UI for breadcrumbs
      beta feature enabled. Otherwise, they should be shown.
     */
    const shouldShowBreadcrumbs = (mainCategoryTags && mainCategoryTags.tags)
      && (!isBreadcrumbsWithNewUI(betaFeatures) || showBreadcrumbsConfig);

    return shouldShowBreadcrumbs && (
      <Breadcrumbs
        className="bop-breadcrumbs"
        pageName={name}
        urls={mainCategoryTags.tags.map((tag, index) =>
          ({
            name: tag.name,
            url: getCatalogueLink({ category: mainCategoryTags.tags.slice(0, index + 1) }, '/browse'),
          }))}
        withNewUI={isBreadcrumbsWithNewUI(betaFeatures)}
      />
    );
  };

  onSeeAlternativesClick = () => {
    const headerNode = document.querySelector('.rs-stickyHeader');

    const relatedFopsTopOffset = document.querySelector('.bop-section.bop-fops')
      .getBoundingClientRect().top + window.scrollY;

    if (!headerNode) {
      return;
    }

    const headerHeight = headerNode.clientHeight;
    window.scroll({
      top: relatedFopsTopOffset - headerHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  getSection = (sectionName) => {
    const { betaFeatures, productBanners, productDetails, router } = this.props;

    const {
      productDetails: {
        criteoSponsoredProducts,
        product = this.props.product,
        recommendations,
      } = {},
    } = this.props;

    const isProductInStock = isInStock(product);
    const categories = get(productDetails, 'backOfPack.tags.categories', []);
    const bopFields = get(productDetails, 'backOfPack.bopFields', {});
    const tags = get(productDetails, 'backOfPack.tags', null);
    const { sku } = product;
    const isFoodMaestroEnabled = (betaFeatures || []).includes(FoodProfileBetaFeatures.FOODMAESTRO_ENABLED);

    switch (sectionName) {
      case 'banners': {
        return (
          <Banners key="bop-banners" betaFeatures={betaFeatures} productBanners={productBanners} />
        );
      }
      case 'advertised-products': {
        return (
          <AdvertisedProducts
            key="bop-ad-products"
            betaFeatures={betaFeatures}
            criteoSponsoredProducts={criteoSponsoredProducts}
          />
        );
      }
      case 'similar-products': {
        return (
          <SimilarProducts
            key="bop-similar-products"
            isProductInStock={isProductInStock}
            productDetails={productDetails}
          />
        );
      }
      case 'product-info': {
        return (
          <ProductInfo
            key="bop-product-info"
            bopFields={bopFields}
            product={product}
            isAlcohol={isAlcohol(categories)}
            isFoodMaestroEnabled={isFoodMaestroEnabled}
          />
        );
      }
      case 'categories': {
        return (<Categories key="bop-categories" tags={tags} />);
      }
      case 'yml': {
        return (<YouMightLike key="bop-yml" recommendations={recommendations} />);
      }
      case 'reviews': {
        return (
          <section key="bop-reviews" id="reviews" className="bop-section bop-reviews">
            <Reviews sku={sku} router={router} />
          </section>
        );
      }
      default:
        return null;
    }
  }

  renderMainSections = () => {
    const { betaFeatures } = this.props;
    const isAlternativeOrder = betaFeatures.includes('BOP_ALTERNATIVE_SECTIONS_ORDER');
    let sectionsOrder = [
      'banners', 'advertised-products', 'similar-products',
      'product-info', 'categories', 'yml', 'reviews',
    ];

    if (isAlternativeOrder) {
      sectionsOrder = [
        'advertised-products', 'similar-products', 'yml',
        'product-info', 'categories', 'banners', 'reviews',
      ];
    }

    return sectionsOrder.map(sectionName => this.getSection(sectionName));
  }

  // eslint-disable-next-line max-lines-per-function
  render() {
    const {
      deliverySlot,
      isLoggedIn,
      feedbackCorrectionTypes,
      turnOffEnforceLocation,
      router,
      postFeedback,
      productsBySku,
      productDetails,
      location,
      isFlashSale,
      betaFeatures,
    } = this.props;

    const {
      analyticsContextData = {},
      productDetails: {
        backOfPack = {},
        recipes,
        similarProducts,
        product = this.props.product,
      } = {},
      pageMetaData = {},
      disclaimerRegularMessage,
    } = this.props;

    if (!product) {
      return (
        <NotFound>
          <Helmet>
            <meta name="robots" content="noindex, follow" />
          </Helmet>
          <p>No product with id &quot;{this.props.params.sku}&quot; has been found.</p>
        </NotFound>
      );
    }

    const { name, sku, packInfo, deranged } = product || {};
    const showLifeDisclaimer = packInfo && (packInfo.minimumShelfLife > 0 || packInfo.averageShelfLife > 0);
    const { images, videos } = backOfPack;
    const derivedSku = product.derivativeSku || product.sku;
    const hasAlternatives = get(similarProducts, 'sections', []).length > 0;
    const isProductInStock = isInStock(product);
    const pageViewGTMData = {
      pageType: pageTypes.BOP,
    };

    return (
      <>
        <PageViewEvent eventData={pageViewGTMData} />
        <PageViewType pageType={pageTypes.BOP} />
        <DynamicYieldPageContext
          type={PageContextType.PRODUCT}
          data={[sku]}
        />
        {!turnOffEnforceLocation && (
          <EnforceLocation href={getEnforcedProductDetailsLink(name, this.props.product.sku, location.search)} />
        )}
        <BopPageComponent product={product} />
        { !isProductInStock && (
          <div className="gn-content">
            <div className="bop-outOfStock__overlay" />
          </div>)
        }

        <article id="overview" className={classNames(['bop', 'gn-content', 'bop-layout'])}>
          {this.renderBreadcrumbs()}

          <section className="bop-section bop-intro">
            <header className="bop-title">
              <Title
                product={product}
                showCatchweight={this.hasDerivatives()}
                router={router}
              />
            </header>
            <div className="bop-gallery">
              <BopGallery
                key={`BOP-GALLERY-${derivedSku}`}
                product={product}
                sku={derivedSku}
                images={images}
                videos={videos}
                packInfo={packInfo}
                betaFeatures={betaFeatures}
              />
            </div>
          </section>

          <BopBasicInfo
            analyticsContextData={analyticsContextData}
            childSku={getChildSku(productDetails)}
            hasAlternatives={hasAlternatives}
            hasDerivatives={this.hasDerivatives()}
            onSeeAlternativesClick={this.onSeeAlternativesClick}
            productsBySku={productsBySku}
            product={this.props.product}
            productDetails={productDetails}
            pageMetaData={pageMetaData}
            router={router}
            sku={sku}
            isFlashSale={isFlashSale}
          />

          {!deranged && this.renderMainSections()}

          <section id="recipes" className="bop-section bop-recipes">
            <RecipesList recipes={recipes} />
          </section>
          <FeedbackForm
            feedbackCorrectionTypes={feedbackCorrectionTypes}
            isLoggedIn={isLoggedIn}
            postFeedback={postFeedback}
            sku={sku}
          />
          <footer className="bop-section bop-disclaimer">
            <Disclaimer
              showLifeDisclaimer={showLifeDisclaimer}
              deliverySlot={deliverySlot}
              priceMessageType={product.price.messageType}
              disclaimerRegularMessage={disclaimerRegularMessage}
            />
          </footer>
        </article>
      </>
    );
  }
}
